import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { updateWorld } from '@/services/worlds'
import type { WorldStatus } from '@/types'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

interface WorldStatusControlProps {
  worldId: string
  currentStatus: WorldStatus
}

const statusConfig: Record<WorldStatus, { label: string; className: string }> = {
  setup: {
    label: 'Setup',
    className: 'bg-gray-100 text-gray-800 border-gray-200',
  },
  active: {
    label: 'Active',
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  paused: {
    label: 'Paused',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  completed: {
    label: 'Completed',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
}

const statusOptions: WorldStatus[] = ['setup', 'active', 'paused', 'completed']

function getStatusChangeMessage(from: WorldStatus, to: WorldStatus): string {
  if (to === 'completed') {
    return 'Game will end permanently. This cannot be undone.'
  }
  if (from === 'setup' && to === 'active') {
    return 'Game will begin. Tick processing will start.'
  }
  if (from === 'active' && to === 'paused') {
    return 'Game will pause. No ticks will process.'
  }
  if (from === 'paused' && to === 'active') {
    return 'Game will resume. Tick processing will continue.'
  }
  if (from === 'paused' && to === 'setup') {
    return 'Game will return to setup mode. No ticks will process.'
  }
  if (from === 'active' && to === 'setup') {
    return 'Game will return to setup mode. Tick processing will stop.'
  }
  return `Status will change from ${statusConfig[from].label} to ${statusConfig[to].label}.`
}

export function WorldStatusControl({ worldId, currentStatus }: WorldStatusControlProps) {
  const queryClient = useQueryClient()
  const [pendingStatus, setPendingStatus] = useState<WorldStatus | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleStatusChange = (newStatus: string) => {
    const status = newStatus as WorldStatus
    if (status !== currentStatus) {
      setPendingStatus(status)
    }
  }

  const handleConfirm = async () => {
    if (!pendingStatus) return

    setIsUpdating(true)
    setError(null)

    try {
      await updateWorld(worldId, { status: pendingStatus })
      await queryClient.invalidateQueries({ queryKey: ['world', worldId] })
      setSuccessMessage(`Status changed to ${statusConfig[pendingStatus].label}`)
      setPendingStatus(null)
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    setPendingStatus(null)
  }

  return (
    <div className="flex items-center gap-2">
      <Select value={currentStatus} onValueChange={handleStatusChange}>
        <SelectTrigger className="w-[130px]">
          <SelectValue>
            <Badge className={cn(statusConfig[currentStatus].className, 'font-normal')}>
              {statusConfig[currentStatus].label}
            </Badge>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {statusOptions.map((status) => (
            <SelectItem key={status} value={status}>
              <Badge className={cn(statusConfig[status].className, 'font-normal')}>
                {statusConfig[status].label}
              </Badge>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {successMessage && <span className="text-sm text-green-600">{successMessage}</span>}

      {error && <span className="text-sm text-red-600">{error}</span>}

      <AlertDialog
        open={pendingStatus !== null}
        onOpenChange={(open: boolean) => !open && handleCancel()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change World Status</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Change status from{' '}
                <Badge className={cn(statusConfig[currentStatus].className, 'font-normal')}>
                  {statusConfig[currentStatus].label}
                </Badge>{' '}
                to{' '}
                {pendingStatus && (
                  <Badge className={cn(statusConfig[pendingStatus].className, 'font-normal')}>
                    {statusConfig[pendingStatus].label}
                  </Badge>
                )}
                ?
              </span>
              <span className="block font-medium">
                {pendingStatus && getStatusChangeMessage(currentStatus, pendingStatus)}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isUpdating}
              className={pendingStatus === 'completed' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {isUpdating ? 'Updating...' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
